import { Text } from '@amelio/ui-library';
import { useEffect, useMemo, useRef } from 'react';

import type { ReviewQuestion, ReviewQuestionOption } from '@/types/review/ReviewQuestion';

import {
    DEFAULT_EMOJIS,
    QuestionEmojiList,
    type EmojiOption,
} from '@/components/QuestionEmojiList/QuestionEmojiList';
import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { getEmojiSrc } from '@/utils/emojis';
import { getTextByLanguage } from '@/utils/localization';

interface EmojiChoiceQuestionProps {
    question: ReviewQuestion;
    setQuestion: (question: ReviewQuestion) => void;
    // Competency-page-only satisfaction labels (best → worst). When provided they replace
    // the shared English DEFAULT_EMOJIS labels; the emoji assets/order stay the same.
    satisfactionLabels?: string[];
}

/* DEFAULT_EMOJIS is ordered best → worst (builder display top → bottom).
 * Persistence flips to the canonical worst → best convention so that:
 *   - sortOrder ASC matches the survey/results horizontal order (worst left → best right)
 *   - scoreValue follows 1 = worst, 5 = best (shared with RatingScale and the seeder) */
const getDefaultEmojiOptions = (language: string, labels?: string[]): ReviewQuestionOption[] =>
    DEFAULT_EMOJIS.map((emoji, index) => ({
        id: crypto.randomUUID(),
        label: { [language]: labels?.[index] ?? emoji.label },
        sortOrder: DEFAULT_EMOJIS.length - 1 - index,
        scoreValue: DEFAULT_EMOJIS.length - index,
    }));

export const EmojiChoiceQuestion = ({
    question,
    setQuestion,
    satisfactionLabels,
}: EmojiChoiceQuestionProps) => {
    const { t } = useAmelioTranslations();
    const defaultLanguage = useSelectedAppLanguage();
    const isMounted = useRef(false);

    const options = question.reviewQuestionOptions ?? [];

    useEffect(() => {
        if (options.length !== 0) {
            return;
        }

        if (isMounted.current) {
            return;
        }

        isMounted.current = true;

        setQuestion({
            ...question,
            reviewQuestionOptions: getDefaultEmojiOptions(defaultLanguage, satisfactionLabels),
        });
    }, [options.length, question, setQuestion, defaultLanguage, satisfactionLabels]);

    /* Builder displays best → worst (top → bottom). Stored options follow the canonical worst → best
     * order, so we flip them by scoreValue DESC for rendering and use the matching emoji asset
     * via getEmojiSrc(scoreValue - 1) — which targets SURVEY_EMOJI_SOURCES (worst at 0). */
    const sortedForDisplay = useMemo(
        () => [...options].sort((a, b) => b.scoreValue - a.scoreValue),
        [options],
    );

    const emojiOptions: EmojiOption[] = useMemo(() => {
        if (options.length === 0) {
            // Mirror getDefaultEmojiOptions so the one-frame fallback already shows the
            // consumer's satisfaction labels (e.g. competency page) instead of the
            // shared English DEFAULT_EMOJIS labels.
            return DEFAULT_EMOJIS.map((emoji, index) => ({
                label: satisfactionLabels?.[index] ?? emoji.label,
                src: emoji.src,
            }));
        }

        return sortedForDisplay.map((option) => ({
            label: getTextByLanguage(option.label, defaultLanguage) || '',
            src: getEmojiSrc(option.scoreValue - 1),
        }));
    }, [options.length, sortedForDisplay, defaultLanguage, satisfactionLabels]);

    const updateOption = (visualIndex: number, value: string) => {
        const target = sortedForDisplay[visualIndex];
        if (!target) {
            return;
        }

        const updated = options.map((option) =>
            option === target
                ? { ...option, label: { ...option.label, [defaultLanguage]: value } }
                : option,
        );
        setQuestion({
            ...question,
            reviewQuestionOptions: updated,
        });
    };

    return (
        <div>
            <Text
                className={'mt-8'}
                weight={'medium'}
            >
                {t('evaluation360Questionnaire.questionForm.questions.emojiChoice.longTitle')}
            </Text>

            <div className={'mt-6 max-w-80'}>
                <QuestionEmojiList
                    emojis={emojiOptions}
                    onLabelUpdate={(args) => updateOption(args.index, args.value)}
                />
            </div>
        </div>
    );
};
